/**
 * Central icon shim — Google Material Icons (react-icons/md) exposed under the
 * lucide-react names the app already uses. A Vite alias points `lucide-react`
 * at this module (see vite.config.ts `resolve.alias`), so every existing
 * `import { X } from "lucide-react"` keeps working unchanged while rendering
 * Material icons.
 *
 * Why a shim, not a codemod: 120+ files import icons and many pass them as
 * component references (`icon={FileText}`, the MODULE_ICONS map, `<EmptyState
 * icon=…/>`, the Sidebar nav config). Keeping every export a real component
 * leaves all of those call sites untouched.
 *
 * Sizing/parity: Material icons here render as SVG, so the app's Tailwind
 * `h-4 w-4` sizing classes work exactly as they did for lucide. We default to
 * `h-5 w-5` (a sensible Material default) merged via cn/tailwind-merge so the
 * rare call site that sets no size class still renders at a sane size, and any
 * `h-4` / `w-4` / `size-4` the call site DOES pass still wins.
 *
 * To revert the whole migration: delete the `lucide-react` alias in
 * vite.config.ts (call sites resolve back to the real package).
 */
import { type ReactElement, type SVGProps } from "react";
import type { IconType } from "react-icons";
import {
  MdAccessTime,
  MdAccountCircle,
  MdAdd,
  MdAlarm,
  MdAlternateEmail,
  MdApartment,
  MdArchive,
  MdArrowBack,
  MdArrowDownward,
  MdArrowForward,
  MdArrowRightAlt,
  MdArrowUpward,
  MdArticle,
  MdAssignment,
  MdAssignmentTurnedIn,
  MdAutoAwesome,
  MdBarChart,
  MdBolt,
  MdBook,
  MdBrokenImage,
  MdBuild,
  MdCalendarToday,
  MdCancel,
  MdCellTower,
  MdChatBubbleOutline,
  MdCheck,
  MdCheckBox,
  MdCheckCircle,
  MdChevronLeft,
  MdChevronRight,
  MdCircle,
  MdClose,
  MdConfirmationNumber,
  MdContentCopy,
  MdCreditCard,
  MdDarkMode,
  MdDashboard,
  MdDeleteOutline,
  MdDescription,
  MdDesignServices,
  MdDesktopWindows,
  MdDns,
  MdDownload,
  MdDriveFileRenameOutline,
  MdDynamicFeed,
  MdEdit,
  MdEditNote,
  MdEmail,
  MdEmojiEvents,
  MdErrorOutline,
  MdEvent,
  MdExplore,
  MdFactCheck,
  MdFilterList,
  MdFindInPage,
  MdFlag,
  MdFormatBold,
  MdFormatItalic,
  MdFormatListBulleted,
  MdFormatListNumbered,
  MdFunctions,
  MdGppBad,
  MdGppMaybe,
  MdGpsFixed,
  MdGroup,
  MdHeadset,
  MdHistory,
  MdHistoryEdu,
  MdImage,
  MdInbox,
  MdInfoOutline,
  MdKeyboardArrowDown,
  MdKeyboardArrowUp,
  MdLanguage,
  MdLaptop,
  MdLayers,
  MdLibraryBooks,
  MdLightMode,
  MdLink,
  MdLinkOff,
  MdLocalLibrary,
  MdLock,
  MdLogout,
  MdLooks3,
  MdLooksOne,
  MdLooksTwo,
  MdMarkEmailUnread,
  MdMenu,
  MdMenuBook,
  MdMic,
  MdMilitaryTech,
  MdMoreHoriz,
  MdMoreVert,
  MdMovie,
  MdNorthEast,
  MdNoteAdd,
  MdOpenInFull,
  MdOpenInNew,
  MdPalette,
  MdPause,
  MdPauseCircle,
  MdPerson,
  MdPersonAdd,
  MdPhotoCamera,
  MdPlayArrow,
  MdPlayCircle,
  MdPrint,
  MdPublic,
  MdQuiz,
  MdRadio,
  MdRadioButtonChecked,
  MdReceiptLong,
  MdRefresh,
  MdRestartAlt,
  MdSave,
  MdSchool,
  MdSearch,
  MdSecurity,
  MdSend,
  MdSettings,
  MdShowChart,
  MdSpeed,
  MdStopCircle,
  MdStorage,
  MdSupport,
  MdSwapVert,
  MdSync,
  MdTableChart,
  MdTune,
  MdUnarchive,
  MdUndo,
  MdUnfoldMore,
  MdUpload,
  MdVerified,
  MdVerifiedUser,
  MdVideocam,
  MdVideocamOff,
  MdViewColumn,
  MdVisibility,
  MdVisibilityOff,
  MdVolumeUp,
  MdVpnKey,
  MdWarning,
  MdWifiOff,
  MdWorkspacePremium,
} from "react-icons/md";
import { cn } from "@/lib/utils";

/** Component type behind the old `LucideIcon` type import (4 call sites use it
 *  to type icon-reference props/maps). Any of these exports satisfies it. */
export type LucideIcon = (props: SVGProps<SVGSVGElement>) => ReactElement;

type IconProps = SVGProps<SVGSVGElement> & { title?: string };

/** Wrap a Material icon so it honours the app's Tailwind size classes and
 *  defaults to a sane size when none is given. `className` is merged LAST-wins
 *  via tailwind-merge, so a call-site `h-4 w-4`/`size-4` overrides the default. */
function make(Icon: IconType): (props: IconProps) => ReactElement {
  const Wrapped = ({ className, ...rest }: IconProps) => (
    <Icon className={cn("h-5 w-5 shrink-0", className)} {...rest} />
  );
  Wrapped.displayName = `Icon(${Icon.name || "md"})`;
  return Wrapped;
}

// ── lucide name → Material icon ──────────────────────────────────────────────
export const Activity = make(MdShowChart);
export const AlarmClock = make(MdAlarm);
export const AlertCircle = make(MdErrorOutline);
export const AlertTriangle = make(MdWarning);
export const Archive = make(MdArchive);
export const ArchiveRestore = make(MdUnarchive);
export const ArrowDown = make(MdArrowDownward);
export const ArrowLeft = make(MdArrowBack);
export const ArrowRight = make(MdArrowForward);
export const ArrowUp = make(MdArrowUpward);
export const ArrowUpDown = make(MdSwapVert);
export const ArrowUpRight = make(MdNorthEast);
export const AtSign = make(MdAlternateEmail);
export const Award = make(MdMilitaryTech);
export const BadgeCheck = make(MdVerified);
export const BarChart3 = make(MdBarChart);
export const BookOpen = make(MdMenuBook);
export const BookText = make(MdBook);
export const Building2 = make(MdApartment);
export const Calendar = make(MdCalendarToday);
export const CalendarClock = make(MdEvent);
export const Camera = make(MdPhotoCamera);
export const Check = make(MdCheck);
export const CheckCircle2 = make(MdCheckCircle);
export const CheckSquare = make(MdCheckBox);
export const ChevronDown = make(MdKeyboardArrowDown);
export const ChevronLeft = make(MdChevronLeft);
export const ChevronRight = make(MdChevronRight);
export const ChevronUp = make(MdKeyboardArrowUp);
export const ChevronsUpDown = make(MdUnfoldMore);
export const Circle = make(MdCircle);
export const CircleAlert = make(MdErrorOutline);
export const CircleDot = make(MdRadioButtonChecked);
export const ClipboardCheck = make(MdAssignmentTurnedIn);
export const ClipboardList = make(MdAssignment);
export const Clock = make(MdAccessTime);
export const Columns3 = make(MdViewColumn);
export const Compass = make(MdExplore);
export const Copy = make(MdContentCopy);
export const CreditCard = make(MdCreditCard);
export const Crown = make(MdWorkspacePremium);
export const Database = make(MdStorage);
export const Download = make(MdDownload);
export const ExternalLink = make(MdOpenInNew);
export const Eye = make(MdVisibility);
export const EyeOff = make(MdVisibilityOff);
export const FileCheck2 = make(MdFactCheck);
export const FileEdit = make(MdEditNote);
export const FilePlus2 = make(MdNoteAdd);
export const FileQuestion = make(MdQuiz);
export const FileSearch = make(MdFindInPage);
export const FileSignature = make(MdHistoryEdu);
export const FileSpreadsheet = make(MdTableChart);
export const FileText = make(MdDescription);
export const Film = make(MdMovie);
export const Filter = make(MdFilterList);
export const Flag = make(MdFlag);
export const Gauge = make(MdSpeed);
export const Globe = make(MdPublic);
export const Globe2 = make(MdLanguage);
export const GraduationCap = make(MdSchool);
export const Headphones = make(MdHeadset);
export const History = make(MdHistory);
export const Image = make(MdImage);
export const ImageIcon = make(MdImage);
export const ImageOff = make(MdBrokenImage);
export const Inbox = make(MdInbox);
export const Info = make(MdInfoOutline);
export const KeyRound = make(MdVpnKey);
export const Laptop = make(MdLaptop);
export const Layers = make(MdLayers);
export const LayoutDashboard = make(MdDashboard);
export const Library = make(MdLocalLibrary);
export const LibraryBig = make(MdLibraryBooks);
export const LifeBuoy = make(MdSupport);
export const LineChart = make(MdShowChart);
export const Link2 = make(MdLink);
// Markdown-editor formatting toolbar (components/MarkdownEditor.tsx). Material
// has no h1/h2/h3 glyphs, so the Looks* numerals stand in: they read as "level
// 1/2/3" and each button carries an explicit aria-label anyway.
export const Bold = make(MdFormatBold);
export const Italic = make(MdFormatItalic);
export const Heading1 = make(MdLooksOne);
export const Heading2 = make(MdLooksTwo);
export const Heading3 = make(MdLooks3);
export const List = make(MdFormatListBulleted);
export const ListOrdered = make(MdFormatListNumbered);
export const Loader2 = make(MdSync);
export const Lock = make(MdLock);
export const LogOut = make(MdLogout);
export const Mail = make(MdEmail);
export const MailWarning = make(MdMarkEmailUnread);
export const Maximize2 = make(MdOpenInFull);
export const Menu = make(MdMenu);
export const MessageSquareText = make(MdChatBubbleOutline);
export const Mic = make(MdMic);
export const Monitor = make(MdDesktopWindows);
export const Moon = make(MdDarkMode);
export const MoreHorizontal = make(MdMoreHoriz);
export const MoreVertical = make(MdMoreVert);
export const MoveRight = make(MdArrowRightAlt);
export const Palette = make(MdPalette);
export const Pause = make(MdPause);
export const PauseCircle = make(MdPauseCircle);
export const PenLine = make(MdEdit);
export const Pencil = make(MdEdit);
export const PencilRuler = make(MdDesignServices);
export const Play = make(MdPlayArrow);
export const PlayCircle = make(MdPlayCircle);
export const Plus = make(MdAdd);
export const Printer = make(MdPrint);
export const Radio = make(MdRadio);
export const RadioTower = make(MdCellTower);
export const Receipt = make(MdReceiptLong);
export const RefreshCcw = make(MdRefresh);
export const RefreshCw = make(MdRefresh);
export const RotateCcw = make(MdRestartAlt);
export const Save = make(MdSave);
export const ScrollText = make(MdArticle);
export const Search = make(MdSearch);
export const Send = make(MdSend);
export const ServerCog = make(MdDns);
export const Settings = make(MdSettings);
export const Settings2 = make(MdTune);
export const Shield = make(MdSecurity);
export const ShieldAlert = make(MdGppMaybe);
export const ShieldCheck = make(MdVerifiedUser);
export const ShieldX = make(MdGppBad);
export const Sigma = make(MdFunctions);
export const Sliders = make(MdTune);
export const Sparkles = make(MdAutoAwesome);
export const SquarePen = make(MdDriveFileRenameOutline);
export const SquareStack = make(MdDynamicFeed);
export const StopCircle = make(MdStopCircle);
export const Sun = make(MdLightMode);
export const Target = make(MdGpsFixed);
export const Ticket = make(MdConfirmationNumber);
export const Trash2 = make(MdDeleteOutline);
export const Trophy = make(MdEmojiEvents);
export const Undo2 = make(MdUndo);
export const Unlink = make(MdLinkOff);
export const Upload = make(MdUpload);
export const User = make(MdPerson);
export const UserCircle = make(MdAccountCircle);
export const UserPlus = make(MdPersonAdd);
export const Users = make(MdGroup);
export const Video = make(MdVideocam);
export const VideoOff = make(MdVideocamOff);
export const Volume2 = make(MdVolumeUp);
export const WifiOff = make(MdWifiOff);
export const Wrench = make(MdBuild);
export const X = make(MdClose);
export const XCircle = make(MdCancel);
export const Zap = make(MdBolt);
